import { AfterViewInit, Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { AnalyticEvent, AnalyticsBloc, AnalyticsPageName } from '@kanalytics';
import { CardBloc, DetailCardData } from '@kp/shared/components/cards/card.bloc';
import { CardItem, JsonObject } from '@ktypes/models';
import { DateTimeUtil } from '@kutil';
import { Subscription } from 'rxjs';

@Component({
  selector: 'kp-audio-player',
  templateUrl: './audio-player.component.html',
  styleUrls: ['./audio-player.component.scss'],
})
export class AudioPlayerComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() analyticsPageName: AnalyticsPageName;
  @Input() card: CardItem;
  @Input() title: string;
  @Input() url: string;

  @ViewChild('bar') bar: ElementRef;
  @ViewChild('buffer') buffer: ElementRef;
  @ViewChild('player') player: ElementRef;
  @ViewChild('progress') progress: ElementRef;

  currentTime = '00:00';
  current: number;
  dur: number;
  duration = '00:00';

  private _analyticsJson: JsonObject = {};
  private _sentEvents: JsonObject = {};
  private _shouldPlay = false;
  private _subscriptions: Subscription[] = [];

  audioPlayButtonElement: HTMLElement;
  @ViewChild('audioPlayButton') set audioPlayButton(value: ElementRef) {
    if (value) {
      this.audioPlayButtonElement = value.nativeElement;
    }
  }

  audioPauseButtonElement: HTMLElement;
  @ViewChild('audioPauseButton') set audioPauseButton(value: ElementRef) {
    if (value) {
      this.audioPauseButtonElement = value.nativeElement;
    }
  }

  constructor(private _analyticsBloc: AnalyticsBloc, private _cardBloc: CardBloc) {}

  ngOnInit() {
    this._analyticsJson['title'] = this.title;
    this._analyticsJson['url'] = this.url;
    this._sentEvents[AnalyticEvent.audio_start] = false;
    this._sentEvents['audio_progress_25'] = false;
    this._sentEvents['audio_progress_50'] = false;
    this._sentEvents['audio_progress_75'] = false;
    this._sentEvents[AnalyticEvent.audio_end] = false;

    this._subscriptions.push(
      this._cardBloc.detailViewClicked.subscribe((data: DetailCardData) => {
        if (data) {
          this._shouldPlay = data.audio;
        }
      })
    );
  }

  ngAfterViewInit() {
    setTimeout(() => {
      if (this._shouldPlay) {
        this.player.nativeElement.play();
      }
    }, 0);
  }

  ngOnDestroy() {
    if (!this._sentEvents[AnalyticEvent.audio_end]) {
      this._trackAudioProgress(AnalyticEvent.audio_end);
      this._sentEvents[AnalyticEvent.audio_end] = true;
    }
    this._subscriptions.forEach((subscription) => subscription.unsubscribe());
    // This line is needed for not playing audio after the component is destroyed.
    // Not destroying the player due to potential issues when clicking back to audio and due to standard set at
    // https://html.spec.whatwg.org/multipage/media.html#best-practices-for-authors-using-media-elements
    this.player.nativeElement.src = '';
  }

  play() {
    if (this.player && !this.isPlaying) {
      this.player.nativeElement.play();
      this._focusPauseButton();
    }
  }

  pause() {
    if (this.player && this.isPlaying) {
      this.player.nativeElement.pause();
      this._focusPlayButton();
    }
  }

  onError() {
    this._focusPlayButton();
  }

  playStarted() {
    this._focusPauseButton();
  }

  paused() {
    this._focusPlayButton();
  }

  getDuration() {
    this.duration = DateTimeUtil.formatSecondsAsTime(this.player.nativeElement.duration) || 'Failed to load audio';
  }

  updateTime() {
    const currentTimeInSec = this.player.nativeElement.currentTime.toFixed();
    if (currentTimeInSec > 0) {
      if (
        this.progressPercentage >= 0 &&
        this.progressPercentage < 25 &&
        !this._sentEvents[AnalyticEvent.audio_start]
      ) {
        this._trackAudioProgress(AnalyticEvent.audio_start);
        this._sentEvents[AnalyticEvent.audio_start] = true;
      } else if (
        this.progressPercentage >= 25 &&
        this.progressPercentage < 50 &&
        !this._sentEvents['audio_progress_25']
      ) {
        this._trackAudioProgress(AnalyticEvent.audio_progress);
        this._sentEvents['audio_progress_25'] = true;
      } else if (
        this.progressPercentage >= 50 &&
        this.progressPercentage < 75 &&
        !this._sentEvents['audio_progress_50']
      ) {
        this._trackAudioProgress(AnalyticEvent.audio_progress);
        this._sentEvents['audio_progress_50'] = true;
      } else if (
        this.progressPercentage >= 75 &&
        this.progressPercentage < 95 &&
        !this._sentEvents['audio_progress_75']
      ) {
        this._trackAudioProgress(AnalyticEvent.audio_progress);
        this._sentEvents['audio_progress_75'] = true;
      }
    }
    const currentTime = this.player.nativeElement.currentTime.toFixed();
    this.currentTime = DateTimeUtil.formatSecondsAsTime(currentTime) || 'Listen';
    this.current = currentTime;
    this.duration = DateTimeUtil.formatSecondsAsTime(this.player.nativeElement.duration) || 'Failed to load audio';

    for (let i = 0; i < this.player.nativeElement.buffered.length; i++) {
      this.updateDuration(this.player.nativeElement.buffered.end(i), this.player.nativeElement.duration);
    }
  }

  seek(event) {
    const percent = event.offsetX / event.target.clientWidth;
    for (let i = 0; i < this.player.nativeElement.buffered.length; i++) {
      this.updateDuration(this.player.nativeElement.buffered.end(i), this.player.nativeElement.duration);
      this.player.nativeElement.currentTime = percent * this.dur;
    }
  }

  updateDuration(bufferedEnd, duration) {
    this.dur = Math.max(bufferedEnd, duration.toFixed());
  }

  ended() {
    if (!this._sentEvents[AnalyticEvent.audio_end]) {
      this._trackAudioProgress(AnalyticEvent.audio_end);
      this._sentEvents[AnalyticEvent.audio_end] = true;
    }
    this.player.nativeElement.currentTime = 0;
  }

  private _focusPlayButton() {
    if (this.audioPlayButtonElement) {
      setTimeout(() => this.audioPlayButtonElement.focus(), 10);
    }
  }

  private _focusPauseButton() {
    if (this.audioPauseButtonElement) {
      setTimeout(() => this.audioPauseButtonElement.focus(), 10);
    }
  }

  private _trackAudioProgress(analyticEvent: AnalyticEvent) {
    this._analyticsJson['progress'] = this.progressPercentage;
    this._analyticsBloc.sendEvent(
      this._analyticsBloc.createEventFromInteraction({
        page: this.analyticsPageName,
        event: analyticEvent,
        meta: this._analyticsJson,
      })
    );
  }

  get progressPercentage(): number {
    return (this.player.nativeElement.currentTime.toFixed() / this.player.nativeElement.duration) * 100;
  }

  get isPlaying(): boolean {
    if (this.player) {
      return (
        this.player.nativeElement.currentTime > 0 &&
        !this.player.nativeElement.paused &&
        !this.player.nativeElement.ended &&
        this.player.nativeElement.readyState > 2
      );
    } else {
      return false;
    }
  }
}
